import React, {useState} from "react";
import {format} from "date-fns";
import {Check, ChevronDown, GitCompareArrows} from "lucide-react";
import {useTranslation} from "react-i18next";
import {useDateFnsLocale} from "../../../i18n/dateLocale";
import {COMPARE_MODES, getComparisonRange} from "../comparison";
import Button from "../../Common/Button";
import MenuSheet from "./MenuSheet";

/**
 * Header dropdown to compare the selected period with the previous period or
 * the same period last year. Styled like QuickPeriodSelector: the selected mode
 * is teal with a check, and each mode shows the dates it compares with.
 */
const ComparePeriodSelector = ({compareMode, setCompareMode, comparisonRange, dateRange, isDarkMode = false}) => {
    const {t} = useTranslation("activite");
    const locale = useDateFnsLocale();
    const [isOpen, setIsOpen] = useState(false);
    const range = comparisonRange?.[0];
    const isActive = compareMode !== "none";

    const formatRange = (r) => {
        if (!r?.startDate || !r?.endDate) return null;
        if (r.startDate.toDateString() === r.endDate.toDateString()) return format(r.startDate, "d MMM yyyy", {locale});
        const sameYear = r.startDate.getFullYear() === r.endDate.getFullYear();
        return `${format(r.startDate, sameYear ? "d MMM" : "d MMM yyyy", {locale})} → ${format(r.endDate, "d MMM yyyy", {locale})}`;
    };
    const rangeText = formatRange(range);
    // Dates each mode would compare with; without the page period, only the active mode's
    const modeRangeText = (mode) => {
        if (mode === "none") return null;
        if (dateRange) return formatRange(getComparisonRange(dateRange, mode)?.[0]);
        return mode === compareMode ? rangeText : null;
    };
    const buttonLabel = isActive ? t(`comparison.modes.${compareMode}`) : t("comparison.label");

    return (
        <MenuSheet
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            title={t("comparison.label")}
            isDarkMode={isDarkMode}
            testId="compare-period-selector"
            trigger={
                <Button
                    variant="plain"
                    className={isActive ? "bg-primary/10" : ""}
                    icon={GitCompareArrows}
                    iconRight={ChevronDown}
                    compact
                    onClick={() => setIsOpen((open) => !open)}
                    aria-haspopup="menu"
                    aria-expanded={isOpen}
                    aria-label={t("comparison.label")}
                    title={rangeText ? `${t("comparison.comparedWith")} ${rangeText}` : t("comparison.label")}
                >
                    {buttonLabel}
                </Button>
            }
        >
            <div className="py-1">
                {COMPARE_MODES.map((mode) => {
                    const selected = compareMode === mode;
                    const modeRange = modeRangeText(mode);
                    return (
                        <button
                            key={mode}
                            type="button"
                            role="menuitemradio"
                            aria-checked={selected}
                            onClick={() => {
                                setCompareMode(mode);
                                setIsOpen(false);
                            }}
                            className={`flex items-center gap-3 w-full text-left px-4 py-2.5 sm:py-2 text-sm transition-colors ${
                                selected
                                    ? "bg-primary/5 text-primary"
                                    : isDarkMode
                                        ? "text-gray-200 hover:bg-gray-700/80"
                                        : "text-gray-700 hover:bg-gray-50"
                            }`}
                        >
                            <span className="flex-1 min-w-0">
                                <span className="block font-medium">{t(`comparison.modes.${mode}`)}</span>
                                {modeRange && (
                                    <span className={`block text-xs mt-0.5 ${
                                        selected ? "text-primary/80" : isDarkMode ? "text-gray-400" : "text-gray-500"
                                    }`}>
                                        {modeRange}
                                    </span>
                                )}
                            </span>
                            {selected && <Check className="w-4 h-4 flex-shrink-0"/>}
                        </button>
                    );
                })}
            </div>
        </MenuSheet>
    );
};

export default ComparePeriodSelector;
